/** Code to map from an object entity type to an icon and metadata. */

import type { LucideIcon as Icon } from "@lucide/svelte";
import Archive from "@lucide/svelte/icons/archive";
import Book from "@lucide/svelte/icons/book";
import Box from "@lucide/svelte/icons/box";
import CloudIcon from "@lucide/svelte/icons/cloud";
import FilePlus from "@lucide/svelte/icons/file-plus";
import HardDrive from "@lucide/svelte/icons/hard-drive";
import Hexagon from "@lucide/svelte/icons/hexagon";
import Image from "@lucide/svelte/icons/image";
import Key from "@lucide/svelte/icons/key";
import Package from "@lucide/svelte/icons/package";
import Shapes from "@lucide/svelte/icons/shapes";
import SquareFunction from "@lucide/svelte/icons/square-function";
import Wifi from "@lucide/svelte/icons/wifi";

import RotatedLayers from "$lib/assets/icons/RotatedLayers.svelte";

/** Matches a Modal object ID like `fc-01KJNEZAG5RVVGN3G2NZYM18SE`. */
const OBJECT_ID_RE = /^[a-z]{2}-\w{10,}$/;

export function isObjectId(value: string): boolean {
  return OBJECT_ID_RE.test(value);
}

/**
 * Prefixes that the /id/:id resolve endpoint can redirect to a dashboard page.
 * Keep in sync with _OBJECT_RESOLVERS in server/modal_server/objects/object.py
 * https://github.com/modal-labs/modal/blob/main/server/modal_server/objects/object.py
 *
 * Supported: ap (App), di (Dict), fu (Function), fc (Function Call),
 *            sb (Sandbox), qu (Queue), st (Secret), ta (Task), vo (Volume)
 */
const RESOLVABLE_PREFIXES = new Set([
  "ap",
  "di",
  "fu",
  "fc",
  "sb",
  "qu",
  "st",
  "ta",
  "vo",
]);

/** Returns true if `value` is an object ID whose prefix the `/id/:id` resolve endpoint supports. */
export function isResolvableObjectId(value: string): boolean {
  return isObjectId(value) && RESOLVABLE_PREFIXES.has(value.slice(0, 2));
}

type ObjectInfo = {
  icon: Icon;
  label: string;
  singular: string;
  plural: string;
};

export function getObjectInfo(prefixOrID: string): ObjectInfo {
  switch (prefixOrID.slice(0, 2)) {
    case "ap":
      return {
        icon: CloudIcon,
        label: "App",
        singular: "an App",
        plural: "Apps",
      };
    case "cs":
      return {
        icon: Shapes,
        label: "Class",
        singular: "a Class",
        plural: "Classes",
      };
    case "di":
      return { icon: Book, label: "Dict", singular: "a Dict", plural: "Dicts" };
    case "fc":
      return {
        icon: SquareFunction,
        label: "Function Call",
        singular: "a Function Call",
        plural: "Function Calls",
      };
    case "fu":
      return {
        icon: SquareFunction,
        label: "Function",
        singular: "a Function",
        plural: "Functions",
      };
    case "im":
      return {
        icon: Image,
        label: "Image",
        singular: "an Image",
        plural: "Images",
      };
    case "mo":
      return {
        icon: FilePlus,
        label: "Mount",
        singular: "a Mount",
        plural: "Mounts",
      };
    case "pr":
      return {
        icon: Wifi,
        label: "Proxy",
        singular: "a Proxy",
        plural: "Proxies",
      };
    case "qu":
      return {
        icon: RotatedLayers,
        label: "Queue",
        singular: "a Queue",
        plural: "Queues",
      };
    case "sb":
      return {
        icon: Package,
        label: "Sandbox",
        singular: "a Sandbox",
        plural: "Sandboxes",
      };
    case "st":
      return {
        icon: Key,
        label: "Secret",
        singular: "a Secret",
        plural: "Secrets",
      };
    case "sv":
      return { icon: Archive, label: "NFS", singular: "an NFS", plural: "NFS" };
    case "ta":
      return {
        icon: Box,
        label: "Container",
        singular: "a Container",
        plural: "Containers",
      };
    case "vo":
      return {
        icon: HardDrive,
        label: "Volume",
        singular: "a Volume",
        plural: "Volumes",
      };
    default:
      return {
        icon: Hexagon,
        label: "Unknown object",
        singular: "an Unknown object",
        plural: "Unknown objects",
      };
  }
}
